export const SIZE = 9;
export const MAX_HEIGHT = 4;
export const SUMMIT = Object.freeze({ r: 4, c: 4 });

const FILES = 'abcdefghi';

export function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < SIZE && c >= 0 && c < SIZE;
}

export function isSummit(r: number, c: number): boolean {
  return r === SUMMIT.r && c === SUMMIT.c;
}

/**
 * Where the summits are: one shared summit at e5, or (experimental) one per camp: near a corner
 * to be reached by the other camp (`twin`), or under each Beacon's start square (`home`).
 */
export type SummitMode = 'center' | 'twin' | 'home';

/** Two summits: Basalt's on b8, Ivory's on h2 (point mirrors of each other). */
export const TWIN_SUMMITS = Object.freeze({
  B: Object.freeze({ r: 1, c: 1 }),
  I: Object.freeze({ r: SIZE - 2, c: SIZE - 2 }),
});

/** Home summits: each Beacon starts on its own peak, Basalt's on e9 and Ivory's on e1. */
export const HOME_SUMMITS = Object.freeze({
  B: Object.freeze({ r: 0, c: 4 }),
  I: Object.freeze({ r: SIZE - 1, c: 4 }),
});

export function summitsOf(mode: SummitMode): { r: number; c: number }[] {
  if (mode === 'twin') return [TWIN_SUMMITS.B, TWIN_SUMMITS.I];
  if (mode === 'home') return [HOME_SUMMITS.B, HOME_SUMMITS.I];
  return [SUMMIT];
}

/** Whether (r, c) is a summit in this mode. Summits can never be raised or lowered. */
export function isSummitSquare(mode: SummitMode, r: number, c: number): boolean {
  return summitsOf(mode).some((s) => s.r === r && s.c === c);
}

/**
 * The summit `team`'s Beacon heads for: the shared one, the other camp's (`twin`), or its own
 * (`home`, where it is a refuge rather than a goal).
 */
export function targetSummit(mode: SummitMode, team: 'I' | 'B'): { r: number; c: number } {
  if (mode === 'center') return SUMMIT;
  if (mode === 'home') return HOME_SUMMITS[team];
  return team === 'I' ? TWIN_SUMMITS.B : TWIN_SUMMITS.I;
}

/** Square name in Halvard notation: (0, 0) is `a9`, (8, 8) is `i1`, the summit is `e5`. */
export function squareName(r: number, c: number): string {
  if (!inBounds(r, c)) throw new RangeError(`Square (${r}, ${c}) is off the board`);
  return `${FILES[c]}${SIZE - r}`;
}

/** Inverse of `squareName`. Throws on anything that is not a board square. */
export function parseSquare(name: string): { r: number; c: number } {
  const match = /^([a-i])([1-9])$/.exec(name);
  if (!match) throw new SyntaxError(`Not a square: "${name}"`);
  const [, file, rank] = match as unknown as [string, string, string];
  return { r: SIZE - Number(rank), c: FILES.indexOf(file) };
}

export function heightAt(heights: number[][], r: number, c: number): number {
  const h = heights[r]?.[c];
  if (h === undefined) throw new RangeError(`Square (${r}, ${c}) is off the board`);
  return h;
}

export function cloneHeights(heights: number[][]): number[][] {
  return heights.map((row) => row.slice());
}

/** Throws unless `heights` is a 9×9 grid of integers from 0 to `maxHeight` (4 by default). */
export function assertHeights(heights: number[][], maxHeight = MAX_HEIGHT): void {
  if (heights.length !== SIZE || heights.some((row) => row.length !== SIZE)) {
    throw new RangeError('Heights must be a 9×9 grid');
  }
  for (const row of heights) {
    for (const h of row) {
      if (!Number.isInteger(h) || h < 0 || h > maxHeight) {
        throw new RangeError(`Invalid height ${h}; heights are integers 0–${maxHeight}`);
      }
    }
  }
}

const CLASSIC: readonly (readonly number[])[] = [
  [0, 0, 1, 1, 0, 1, 1, 0, 0],
  [0, 1, 1, 2, 1, 2, 1, 1, 0],
  [1, 1, 3, 3, 2, 3, 3, 1, 1],
  [1, 2, 3, 1, 3, 1, 3, 2, 1],
  [0, 1, 2, 1, 4, 1, 2, 1, 0],
  [1, 2, 3, 1, 3, 1, 3, 2, 1],
  [1, 1, 3, 3, 2, 3, 3, 1, 1],
  [0, 1, 1, 2, 1, 2, 1, 1, 0],
  [0, 0, 1, 1, 0, 1, 1, 0, 0],
];

/** The fixed Classic mountain, used for tests and tutorials. Returns a fresh copy. */
export function classicBoard(): number[][] {
  return CLASSIC.map((row) => row.slice());
}
